import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Store, Globe, ArrowLeft, Check, X, Ban, Crown, Sparkles, Gift } from 'lucide-react';
import { storesApi } from '../../api/endpoints/stores';
import { ownershipApi, type IncomingTransfer } from '../../api/endpoints/ownership';

// Store ownership transfer is hidden for now (backend stays live). Flip to true
// to re-expose incoming-transfer cards in the store picker.
const STORE_OWNERSHIP_TRANSFER_ENABLED = false;
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Alert';
import { getStoreLogoUrl } from '../../lib/formatters';
import { PlanPaymentModal } from '../../components/expenses/PlanPaymentModal';
import { CreateStoreModal } from '../../components/stores/CreateStoreModal';
import { MEMBERSHIP_PLANS } from '../../lib/constants';
import type { StoreListItem } from '../../types/store';

export function StoreList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [declineTarget, setDeclineTarget] = useState<StoreListItem | null>(null);
  const [planTarget, setPlanTarget] = useState<StoreListItem | null>(null);
  const [showCreateStore, setShowCreateStore] = useState(false);

  const rawPlanParam = searchParams.get('plan');
  // Ignore unknown plan codes (link tampering, removed plans). The chooser
  // modal only opens when we recognize the plan.
  const planParam = rawPlanParam && MEMBERSHIP_PLANS[rawPlanParam] ? rawPlanParam : null;
  // Billing cycle carried from the home pricing page (?cycle=yearly). Defaults
  // to monthly for any other/absent value.
  const cycleParam: 'monthly' | 'yearly' = searchParams.get('cycle') === 'yearly' ? 'yearly' : 'monthly';

  const clearPlanParam = () => {
    setSearchParams((prev) => {
      prev.delete('plan');
      prev.delete('cycle');
      return prev;
    });
  };

  const { data, isLoading } = useQuery({
    queryKey: ['stores'],
    queryFn: storesApi.list,
  });

  const pendingInvitations = data?.pendingInvitations ?? [];
  const findInviteId = (storeId: string) => {
    const inv = pendingInvitations.find(i => i.storeId === storeId);
    return inv?._id ?? storeId;
  };

  const acceptMutation = useMutation({
    mutationFn: (storeId: string) => storesApi.acceptInvitation(storeId, findInviteId(storeId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores'] });
      toast('success', t('stores.acceptSuccess'));
    },
    onError: () => toast('error', t('common.error')),
  });

  const declineMutation = useMutation({
    mutationFn: (storeId: string) => storesApi.declineInvitation(storeId, findInviteId(storeId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores'] });
      toast('success', t('stores.declineSuccess'));
      setDeclineTarget(null);
    },
    onError: () => toast('error', t('common.error')),
  });

  const { data: incomingTransfers } = useQuery({
    queryKey: ['ownership-incoming'],
    queryFn: ownershipApi.incoming,
    enabled: STORE_OWNERSHIP_TRANSFER_ENABLED,
  });

  const acceptTransfer = useMutation({
    mutationFn: (tr: IncomingTransfer) => ownershipApi.accept(tr.storeSlug),
    onSuccess: (_res, tr) => {
      queryClient.invalidateQueries({ queryKey: ['stores'] });
      queryClient.invalidateQueries({ queryKey: ['ownership-incoming'] });
      toast('success', t('ownership.accepted', 'أصبحت الآن مالك المتجر'));
      navigate(`/${tr.storeSlug}`);
    },
    onError: () => toast('error', t('common.error')),
  });

  const declineTransfer = useMutation({
    mutationFn: (tr: IncomingTransfer) => ownershipApi.decline(tr.storeSlug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ownership-incoming'] });
      toast('success', t('ownership.declined', 'تم رفض طلب النقل'));
    },
    onError: () => toast('error', t('common.error')),
  });

  const stores = data?.stores ?? [];

  // The backend returns accepted stores in `stores` and not-yet-accepted
  // invites in a separate `pendingInvitations` array. Merge the invites in as
  // synthetic (non-navigable) cards so the picker actually surfaces
  // Accept/Decline. Without this, an employee who clicks the invite email
  // lands here and sees nothing to act on. Each card's `_id` is set to the
  // invite's storeId so findInviteId() resolves back to the membership id for
  // the accept/decline calls, and `accepted: false` marks it pending.
  const pendingCards: StoreListItem[] = pendingInvitations
    .filter((inv) => !stores.some((s) => s._id === inv.storeId))
    .map((inv) => ({
      _id: inv.storeId,
      id: inv.storeSlug || inv.storeId,
      name: inv.storeName || '',
      logo: inv.logo,
      domain: inv.domain,
      role: inv.role,
      accepted: false,
    }));
  // Pending invites first so they're the obvious thing to act on.
  const displayStores = [...pendingCards, ...stores];

  const MAX_OWNED_STORES = 4;
  const ownedStores = stores.filter((s) => s.role === 'owner').length;

  const roleColor = (role?: string) => {
    switch (role) {
      case 'owner': return { bg: 'rgba(110,184,255,0.12)', text: '#6eb8ff', border: 'rgba(110,184,255,0.25)' };
      case 'admin': return { bg: 'rgba(96,165,250,0.12)', text: '#60a5fa', border: 'rgba(96,165,250,0.25)' };
      case 'manager': return { bg: 'rgba(251,191,36,0.12)', text: '#fbbf24', border: 'rgba(251,191,36,0.25)' };
      case 'staff': return { bg: 'rgba(52,211,153,0.12)', text: '#34d399', border: 'rgba(52,211,153,0.25)' };
      default: return { bg: 'rgba(92,92,110,0.12)', text: '#5c5c6e', border: 'rgba(92,92,110,0.25)' };
    }
  };

  const roleLabel = (role?: string) => {
    if (!role) return '';
    const translated = t(`roles.${role}`, { defaultValue: '' });
    return translated || role;
  };

  const isPending = (store: StoreListItem) => store.accepted === false;

  // Generic deep-link: /dashboard/?to=settings&tab=aiConnectors lands the
  // merchant on a specific in-store page once their store is resolved. `to`
  // is a relative path under /:storeId; any other params (e.g. tab) pass through.
  const deepLinkTo = searchParams.get('to');
  const buildStorePath = (id: string) => {
    const safeTo = (deepLinkTo || '').replace(/^\/+/, '');
    if (!safeTo || !/^[a-zA-Z0-9/_-]+$/.test(safeTo)) return `/${id}`;
    const rest = new URLSearchParams(searchParams);
    rest.delete('to');
    const qs = rest.toString();
    return `/${id}/${safeTo}${qs ? `?${qs}` : ''}`;
  };

  const handleStoreClick = (store: StoreListItem) => {
    if (isPending(store)) return;
    if (planParam) {
      setPlanTarget(store);
      return;
    }
    const id = store.id || store._id;
    navigate(buildStorePath(id));
  };

  // With a deep-link and exactly one accepted store, skip the picker entirely.
  useEffect(() => {
    if (!deepLinkTo || planParam) return;
    const accepted = stores.filter((s) => s.accepted !== false);
    if (accepted.length === 1) {
      const s = accepted[0];
      navigate(buildStorePath(s.id || s._id), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkTo, planParam, stores.length]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-primary">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-primary">
      {/* Ambient glow */}
      <div
        className="pointer-events-none fixed top-0 left-1/2 -translate-x-1/2 h-[600px] w-[800px] opacity-[0.035]"
        style={{
          background: 'radial-gradient(ellipse at center, #6eb8ff 0%, transparent 70%)',
        }}
      />

      <div className="relative mx-auto max-w-5xl px-6 py-16">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="mb-2 text-sm font-medium tracking-wider text-accent" style={{ letterSpacing: '0.08em' }}>
                {t('sidebar.dashboard')}
              </p>
              <h1 className="text-3xl font-bold tracking-tight">
                {t('stores.myStores')}
              </h1>
              <p className="mt-3 text-sm text-text-muted">
                {t('stores.storeCount', { count: stores.length })}
              </p>
            </div>
            {ownedStores >= MAX_OWNED_STORES ? (
              <div className="flex items-center gap-2.5 rounded-xl border border-border-default bg-bg-tertiary px-5 py-3 text-sm font-semibold text-text-muted cursor-not-allowed">
                <Ban className="h-4 w-4" />
                {t('stores.maxStoresReached')}
              </div>
            ) : (
              <button
                onClick={() => setShowCreateStore(true)}
                className="group flex items-center gap-2.5 rounded-xl border border-accent/30 bg-accent/8 px-5 py-3 text-sm font-semibold text-accent transition-all hover:border-accent/50 hover:bg-accent/15 hover:shadow-lg hover:shadow-accent/10 cursor-pointer"
              >
                <Plus className="h-4 w-4" />
                {t('stores.createStore')}
              </button>
            )}
          </div>

          {/* Divider */}
          <div className="mt-8 h-px bg-gradient-to-l from-transparent via-border-default to-transparent" />

          {/* Plan purchase banner */}
          {planParam && (
            <div className="mt-6 flex items-center gap-3 rounded-xl border border-accent/25 bg-accent/10 px-5 py-3.5">
              <Sparkles className="h-5 w-5 shrink-0 text-accent" />
              <p className="flex-1 text-sm font-medium text-accent">
                {t('stores.planBanner', { plan: planParam })}
              </p>
              <button
                onClick={clearPlanParam}
                className="text-sm font-medium text-accent/70 underline underline-offset-2 transition-colors hover:text-accent cursor-pointer"
              >
                {t('common.cancel')}
              </button>
            </div>
          )}
        </div>

        {/* Referral program entry point. This screen is the first thing a
            merchant sees after login, which is the only place the program gets
            real discoverability. */}
        <button
          onClick={() => navigate('/referrals')}
          className="mb-8 flex w-full items-center gap-4 rounded-xl border border-accent/25 bg-accent/8 px-5 py-4 text-start transition-all hover:border-accent/45 hover:bg-accent/12 cursor-pointer"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent">
            <Gift className="h-5 w-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-text-primary">
              {t('referrals.entryTitle', 'ادعُ تجاراً واربح مكافآت')}
            </span>
            <span className="block text-xs text-text-muted">
              {t('referrals.entrySubtitle', 'شارك رابطك، واربح عن كل تاجر ينضم ويبيع')}
            </span>
          </span>
          <ArrowLeft className="h-4 w-4 shrink-0 text-accent ltr:rotate-180" />
        </button>

        {/* Incoming ownership transfers (someone is handing you a store) */}
        {STORE_OWNERSHIP_TRANSFER_ENABLED && (incomingTransfers?.length ?? 0) > 0 && (
          <div className="mb-8">
            <h2 className="mb-3 text-sm font-semibold text-text-muted">
              {t('ownership.incomingTitle', 'طلبات نقل ملكية إليك')}
            </h2>
            <div className="space-y-3">
              {incomingTransfers!.map((tr) => (
                <div
                  key={tr.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-accent/30 bg-accent/8 px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="font-semibold text-text-primary">{tr.storeName}</p>
                    <p className="text-xs text-text-muted">
                      {t('ownership.from', 'من')} <span dir="ltr">{tr.fromEmail}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => declineTransfer.mutate(tr)}
                      disabled={declineTransfer.isPending}
                    >
                      <X className="h-3.5 w-3.5" />
                      {t('stores.decline')}
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => acceptTransfer.mutate(tr)}
                      isLoading={acceptTransfer.isPending && acceptTransfer.variables?.id === tr.id}
                    >
                      <Crown className="h-3.5 w-3.5" />
                      {t('ownership.acceptOwnership', 'قبول الملكية')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Store grid */}
        {displayStores.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 text-center">
            <div
              className="mb-8 flex h-24 w-24 items-center justify-center rounded-2xl"
              style={{
                background: 'linear-gradient(135deg, #16161a 0%, #1e1e24 100%)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 0 0 1px rgba(42,42,50,1)',
              }}
            >
              <Store className="h-10 w-10 text-text-muted" />
            </div>
            <h3 className="text-xl font-semibold">{t('stores.noStores')}</h3>
            <p className="mt-2 max-w-xs text-sm text-text-muted leading-relaxed">
              {t('stores.createYourFirst')}
            </p>
            {ownedStores >= MAX_OWNED_STORES ? (
              <div className="mt-8 flex items-center gap-2 rounded-xl border border-border-default bg-bg-tertiary px-6 py-3 text-sm font-semibold text-text-muted cursor-not-allowed">
                <Ban className="h-4 w-4" />
                {t('stores.maxStoresReached')}
              </div>
            ) : (
              <button
                onClick={() => setShowCreateStore(true)}
                className="mt-8 flex items-center gap-2 rounded-xl bg-accent px-6 py-3 text-sm font-semibold text-bg-primary transition-all hover:bg-accent-hover cursor-pointer"
              >
                <Plus className="h-4 w-4" />
                {t('stores.createStore')}
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {displayStores.map((store, index) => {
              const role = roleColor(store.role);
              const pending = isPending(store);
              return (
                <div
                  key={store._id}
                  onClick={() => handleStoreClick(store)}
                  role={pending ? undefined : 'button'}
                  tabIndex={pending ? undefined : 0}
                  onKeyDown={pending ? undefined : (e) => { if (e.key === 'Enter') handleStoreClick(store); }}
                  className={`group relative rounded-2xl outline-none transition-all duration-300 focus-visible:ring-2 focus-visible:ring-accent/50 ${pending ? '' : 'cursor-pointer hover:-translate-y-1'}`}
                  style={{
                    animationDelay: `${index * 60}ms`,
                  }}
                >
                  {/* Card surface */}
                  <div
                    className="relative overflow-hidden rounded-2xl p-[1px]"
                    style={{
                      background: pending
                        ? 'linear-gradient(180deg, rgba(251,191,36,0.4) 0%, rgba(42,42,50,0.4) 100%)'
                        : planParam
                          ? 'linear-gradient(180deg, rgba(110,184,255,0.5) 0%, rgba(110,184,255,0.15) 100%)'
                          : 'linear-gradient(180deg, rgba(42,42,50,1) 0%, rgba(42,42,50,0.4) 100%)',
                    }}
                  >
                    {/* Hover glow overlay */}
                    <div
                      className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                      style={{
                        background: pending
                          ? 'linear-gradient(180deg, rgba(251,191,36,0.1) 0%, rgba(251,191,36,0) 60%)'
                          : 'linear-gradient(180deg, rgba(110,184,255,0.15) 0%, rgba(110,184,255,0) 60%)',
                      }}
                    />

                    <div className="relative rounded-[15px] bg-bg-secondary p-5">
                      {/* Pending badge */}
                      {pending && (
                        <div className="mb-3">
                          <Badge variant="warning">{t('stores.pendingInvitation')}</Badge>
                        </div>
                      )}

                      {/* Plan purchase badge */}
                      {planParam && !pending && (
                        <div className="mb-3">
                          <Badge variant="accent">
                            <Crown className="mr-1 h-3 w-3" />
                            {t('stores.selectForPlan')}
                          </Badge>
                        </div>
                      )}

                      {/* Top row: Logo + Info */}
                      <div className="flex items-start gap-4">
                        {/* Logo */}
                        <div className="relative shrink-0">
                          {store.logo ? (
                            <img
                              src={getStoreLogoUrl(store.logo, 120)}
                              alt={store.name}
                              className="h-[52px] w-[52px] rounded-xl object-cover"
                              style={{
                                boxShadow: '0 0 0 1px rgba(42,42,50,1), 0 2px 8px rgba(0,0,0,0.3)',
                              }}
                            />
                          ) : (
                            <div
                              className="flex h-[52px] w-[52px] items-center justify-center rounded-xl text-lg font-bold text-accent"
                              style={{
                                background: 'linear-gradient(135deg, rgba(110,184,255,0.15) 0%, rgba(110,184,255,0.05) 100%)',
                                boxShadow: '0 0 0 1px rgba(110,184,255,0.15), 0 2px 8px rgba(0,0,0,0.2)',
                              }}
                            >
                              {store.name?.charAt(0) ?? '?'}
                            </div>
                          )}
                        </div>

                        {/* Store info */}
                        <div className="min-w-0 flex-1 pt-0.5">
                          <h3 className={`truncate text-[15px] font-semibold leading-snug text-text-primary transition-colors ${pending ? '' : 'group-hover:text-accent'}`}>
                            {store.name}
                          </h3>
                          {store.domain && (
                            <div className="mt-1.5 flex items-center gap-1.5">
                              <Globe className="h-3 w-3 shrink-0 text-text-muted" />
                              <span className="truncate text-xs text-text-muted">
                                {store.domain}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Bottom row */}
                      {pending ? (
                        <div className="mt-4 flex items-center gap-2 border-t border-border-default pt-4">
                          {store.role && (
                            <span
                              className="inline-flex items-center rounded-lg px-2.5 py-1 text-[11px] font-semibold tracking-wide"
                              style={{
                                backgroundColor: role.bg,
                                color: role.text,
                                border: `1px solid ${role.border}`,
                              }}
                            >
                              {roleLabel(store.role)}
                            </span>
                          )}
                          <div className="flex-1" />
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); setDeclineTarget(store); }}
                            disabled={declineMutation.isPending}
                          >
                            <X className="h-3.5 w-3.5" />
                            {t('stores.decline')}
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); acceptMutation.mutate(store._id); }}
                            isLoading={acceptMutation.isPending && acceptMutation.variables === store._id}
                            disabled={acceptMutation.isPending && acceptMutation.variables !== store._id}
                          >
                            <Check className="h-3.5 w-3.5" />
                            {t('stores.accept')}
                          </Button>
                        </div>
                      ) : (
                        <div className="mt-4 flex items-center justify-between">
                          {store.role ? (
                            <span
                              className="inline-flex items-center rounded-lg px-2.5 py-1 text-[11px] font-semibold tracking-wide"
                              style={{
                                backgroundColor: role.bg,
                                color: role.text,
                                border: `1px solid ${role.border}`,
                              }}
                            >
                              {roleLabel(store.role)}
                            </span>
                          ) : (
                            <span />
                          )}
                          <div className="flex h-7 w-7 items-center justify-center rounded-lg text-text-muted transition-all duration-300 group-hover:text-accent group-hover:translate-x-[-2px]">
                            <ArrowLeft className="h-4 w-4" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Decline confirmation modal */}
      <Modal
        isOpen={!!declineTarget}
        onClose={() => setDeclineTarget(null)}
        title={t('stores.decline')}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeclineTarget(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              isLoading={declineMutation.isPending}
              onClick={() => declineTarget && declineMutation.mutate(declineTarget._id)}
            >
              {t('stores.decline')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-text-secondary">{t('stores.declineConfirm')}</p>
        <p className="mt-2 font-medium">{declineTarget?.name}</p>
        <p className="mt-2 text-xs text-text-muted">{t('stores.declineConfirmDesc')}</p>
      </Modal>

      {/* Plan purchase chooser (wallet vs. card).
          Note: planParam is read once on open. Don't gate the modal on the
          live `planParam` value — clearing the URL param mid-success view
          would unmount the modal before the user sees the success screen.
          The modal stays mounted until the user closes it; only then do we
          drop the ?plan= query and the toast/refresh. */}
      {planTarget && planParam && (
        <PlanPaymentModal
          storeId={(planTarget.id || planTarget._id) as string}
          storeName={planTarget.name}
          plan={planParam}
          cycle={cycleParam}
          onClose={() => {
            setPlanTarget(null);
            clearPlanParam();
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['stores'] });
            toast('success', t('stores.planActivated'));
          }}
        />
      )}

      <CreateStoreModal
        isOpen={showCreateStore}
        onClose={() => setShowCreateStore(false)}
      />
    </div>
  );
}
